import React from 'react';
import PropTypes from 'prop-types';
import {
  FaShieldAlt,
  FaCalendarAlt,
  FaBuilding,
  FaSitemap,
  FaSyncAlt,
  FaProjectDiagram,
  FaRocket,
  FaCheckCircle,
  FaTimesCircle,
  FaChartLine,
  FaTimes,
  FaStream,
  FaFilter,
  FaCubes,
  FaHeartbeat,
  FaChartPie,
  FaChevronDown
} from 'react-icons/fa';


export default function IapUsageSkeleton({ featureName = 'IAP Analytics' }) {
  return (
    <div
      className="skeleton-wireframe w-full flex flex-col gap-4 text-slate-800"
      aria-hidden="true"
      data-testid="iap-usage-skeleton"
    >
      {/* 1. Hub Header */}
      <div className="flex items-center justify-between pb-3 border-b border-slate-200">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">IAP Analytics</h1>
          <p className="text-xs text-slate-500">
            Unified operational, adoption, and ingestion metrics across digital workflows and runtime modules
          </p>
        </div>
        <div className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-indigo-50 border border-indigo-200 rounded-full text-indigo-700 text-xs font-semibold">
          <FaShieldAlt className="text-indigo-600" /> Advanced Analytics Tier
        </div>
      </div>

      {/* 2. Filter Bar */}
      <div className="bg-white border border-slate-200 rounded-xl p-3 flex flex-wrap items-center gap-4 shadow-sm text-xs">
        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FaBuilding className="text-slate-400" /> Organization
          </span>
          <div className="flex items-center justify-between gap-3 bg-white border border-slate-300 rounded-md px-3 py-1.5 min-w-[130px] text-slate-800 font-medium">
            <span>Client Site</span>
            <FaChevronDown className="text-[9px] text-slate-400" />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FaSitemap className="text-slate-400" /> Site Collection
          </span>
          <div className="flex items-center justify-between gap-3 bg-white border border-slate-300 rounded-md px-3 py-1.5 min-w-[160px] text-slate-800 font-medium">
            <span>Client Demonstration</span>
            <FaChevronDown className="text-[9px] text-slate-400" />
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1.5">
            <FaCalendarAlt className="text-slate-400" /> Range:
          </span>
          <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-md">
            <span className="px-2.5 py-1 text-slate-600 font-medium text-xs">Today</span>
            <span className="px-2.5 py-1 text-slate-600 font-medium text-xs">3D</span>
            <span className="px-2.5 py-1 bg-indigo-600 text-white font-bold rounded shadow-xs text-xs">7D</span>
            <span className="px-2.5 py-1 text-slate-600 font-medium text-xs">30D</span>
            <span className="px-2.5 py-1 text-slate-600 font-medium text-xs">Custom</span>
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">From</span>
          <div className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-slate-700 min-w-[110px]">
            16-09-2026
          </div>
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">To</span>
          <div className="bg-white border border-slate-300 rounded-md px-3 py-1.5 text-slate-700 min-w-[110px]">
            23-09-2026
          </div>
        </div>

        <button
          type="button"
          tabIndex={-1}
          className="ml-auto inline-flex items-center gap-1.5 px-3.5 py-1.5 bg-white border border-slate-300 rounded-md text-slate-700 font-semibold hover:bg-slate-50 text-xs shadow-xs"
        >
          <FaSyncAlt /> Refresh
        </button>
      </div>

      {/* 3. Navigation Tabs */}
      <div className="flex items-center gap-4 border-b-2 border-slate-200 text-xs font-semibold">
        <span className="inline-flex items-center gap-2 py-2 text-slate-500">
          <FaChartPie /> Overview
        </span>
        <span className="inline-flex items-center gap-2 py-2 text-indigo-600 border-b-2 border-indigo-600 -mb-[2px]">
          <FaProjectDiagram /> Flow Adoption
        </span>
        <span className="inline-flex items-center gap-2 py-2 text-slate-500">
          <FaCubes /> Module Usage
        </span>
        <span className="inline-flex items-center gap-2 py-2 text-slate-500">
          <FaHeartbeat /> Pipeline Health
        </span>
      </div>

      {/* 4. Comparison Subheader Card */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-sm font-bold text-slate-900">Flow Journey Funnel Telemetry</h2>
            <p className="text-xs text-slate-500">
              Track multi-step completion funnels, user transition drop-offs, and journey friction points.
            </p>
          </div>
          <div className="flex items-center gap-2 text-xs">
            <span className="px-3 py-1 bg-indigo-600 text-white font-semibold rounded-md shadow-xs">
              Stack All (3)
            </span>
            <span className="px-3 py-1 bg-slate-100 text-slate-600 font-semibold rounded-md">
              Single Focus
            </span>
            <span className="px-3 py-1 bg-slate-100 text-slate-600 font-semibold rounded-md inline-flex items-center gap-1">
              <FaSyncAlt /> Top 3
            </span>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 text-xs">
          <span className="font-semibold text-slate-500">Comparing 3 of max 5:</span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-md font-semibold text-[11px]">
            #1 Demo Resource Group <FaTimes />
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-md font-semibold text-[11px]">
            #2 Page Load Alert <FaTimes />
          </span>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-indigo-50 text-indigo-800 border border-indigo-200 rounded-md font-semibold text-[11px]">
            #3 SQL Guide <FaTimes />
          </span>
          <span className="inline-flex items-center gap-1 px-2.5 py-1 border border-dashed border-slate-300 text-slate-500 rounded-md text-[11px]">
            + Add Flow to Compare... <FaChevronDown className="text-[9px]" />
          </span>
        </div>
      </div>

      {/* 5. Main Sankey Card */}
      <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm flex flex-col gap-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center text-lg shadow-xs">
              <FaProjectDiagram />
            </div>
            <div>
              <h3 className="text-base font-bold text-slate-900 leading-tight">Demo Resource Group</h3>
              <p className="text-xs text-slate-500">Step-by-step progression, transition volumes, and drop-off rates</p>
            </div>
          </div>

          <div className="flex items-center gap-2 text-xs">
            <div className="flex flex-col items-center bg-slate-100 px-3.5 py-1.5 rounded-lg min-w-[70px]">
              <span className="text-[9px] font-bold text-slate-600 uppercase tracking-wider flex items-center gap-1">
                <FaRocket /> Launched
              </span>
              <span className="text-sm font-extrabold text-slate-800">40</span>
            </div>
            <div className="flex flex-col items-center bg-emerald-50 px-3.5 py-1.5 rounded-lg min-w-[70px]">
              <span className="text-[9px] font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-1">
                <FaCheckCircle /> Completed
              </span>
              <span className="text-sm font-extrabold text-emerald-700">0</span>
            </div>
            <div className="flex flex-col items-center bg-rose-50 px-3.5 py-1.5 rounded-lg min-w-[70px]">
              <span className="text-[9px] font-bold text-rose-700 uppercase tracking-wider flex items-center gap-1">
                <FaTimesCircle /> Dropped
              </span>
              <span className="text-sm font-extrabold text-rose-700">40</span>
            </div>
            <div className="flex flex-col items-center bg-indigo-50 px-3.5 py-1.5 rounded-lg min-w-[70px]">
              <span className="text-[9px] font-bold text-indigo-700 uppercase tracking-wider flex items-center gap-1">
                <FaChartLine /> Rate
              </span>
              <span className="text-sm font-extrabold text-indigo-700">0%</span>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between border-y border-slate-100 py-2 text-xs">
          <div className="inline-flex rounded-lg border border-slate-200 overflow-hidden">
            <span className="px-3 py-1.5 bg-indigo-50 text-indigo-700 font-bold inline-flex items-center gap-1.5">
              <FaStream /> Journey Flow
            </span>
            <span className="px-3 py-1.5 bg-white text-slate-600 font-medium inline-flex items-center gap-1.5">
              <FaFilter /> Step Funnel
            </span>
          </div>

          <div className="flex items-center gap-2 text-slate-500">
            <span>Box Height:</span>
            <span className="px-2 py-0.5 bg-slate-100 rounded text-[11px]">440px</span>
            <span className="px-2 py-0.5 bg-slate-800 text-white font-bold rounded text-[11px]">560px</span>
            <span className="px-2 py-0.5 bg-slate-100 rounded text-[11px]">680px</span>
          </div>
        </div>

        {/* 6. Colorful Sankey SVG Diagram */}
        <div className="w-full h-[460px] bg-white rounded-xl border border-slate-100 overflow-hidden flex items-center justify-center">
          <svg viewBox="0 0 1000 480" className="w-full h-full block" preserveAspectRatio="none">
            <defs>
              <linearGradient id="gradient-massive-drop" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#475569" stopOpacity="0.4" />
                <stop offset="25%" stopColor="#f87171" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#ef4444" stopOpacity="0.6" />
              </linearGradient>

              <linearGradient id="gradient-flow-progression" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#6366f1" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#818cf8" stopOpacity="0.45" />
              </linearGradient>

              <linearGradient id="gradient-step-drop" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#818cf8" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#ef4444" stopOpacity="0.55" />
              </linearGradient>
            </defs>

            {/* Massive Drop-off ribbon from Start to Top-Right Exit */}
            <path
              d="M 66 60 C 400 60, 500 60, 850 60 L 850 280 C 500 280, 400 200, 66 200 Z"
              fill="url(#gradient-massive-drop)"
            />

            {/* Start Node */}
            <rect x="50" y="60" width="16" height="260" rx="3" fill="#475569" />
            <text x="30" y="195" fill="#475569" fontSize="12" fontWeight="700">start</text>

            {/* Top Exit Terminal Bar (Abandoned before Step 1) */}
            <rect x="850" y="60" width="16" height="220" rx="3" fill="#ef4444" />
            <text x="874" y="170" fill="#dc2626" fontSize="12" fontWeight="700">Abandoned before Step 1</text>

            {/* Progression Stream into Step 1 */}
            <path
              d="M 66 220 C 95 220, 105 270, 140 270 L 140 315 C 105 315, 95 260, 66 260 Z"
              fill="url(#gradient-flow-progression)"
            />

            {/* Step 1 Node */}
            <rect x="140" y="270" width="16" height="45" rx="3" fill="#6366f1" />
            <text x="115" y="295" fill="#4f46e5" fontSize="11" fontWeight="700">Step 1</text>

            {/* Drop-off from Step 1 to Exit */}
            <path
              d="M 156 270 C 450 270, 600 300, 850 300 L 850 312 C 600 312, 450 285, 156 285 Z"
              fill="url(#gradient-step-drop)"
            />
            <rect x="850" y="300" width="16" height="12" rx="2" fill="#ef4444" />
            <text x="874" y="310" fill="#dc2626" fontSize="11" fontWeight="600">Dropped off at Step 1</text>

            {/* Progression Step 1 -> Step 2 */}
            <path
              d="M 156 285 C 185 285, 195 295, 230 295 L 230 330 C 195 330, 185 315, 156 315 Z"
              fill="url(#gradient-flow-progression)"
            />

            {/* Step 2 Node */}
            <rect x="230" y="295" width="16" height="35" rx="3" fill="#6366f1" />
            <text x="205" y="318" fill="#4f46e5" fontSize="11" fontWeight="700">Step 2</text>

            {/* Drop-off from Step 2 to Exit */}
            <path
              d="M 246 295 C 500 295, 650 328, 850 328 L 850 340 C 650 340, 500 310, 246 310 Z"
              fill="url(#gradient-step-drop)"
            />
            <rect x="850" y="328" width="16" height="12" rx="2" fill="#ef4444" />
            <text x="874" y="338" fill="#dc2626" fontSize="11" fontWeight="600">Dropped off at Step 2</text>

            {/* Progression Step 2 -> Step 3 */}
            <path
              d="M 246 310 C 275 310, 285 320, 320 320 L 320 340 C 285 340, 275 330, 246 330 Z"
              fill="url(#gradient-flow-progression)"
            />

            {/* Step 3 Node */}
            <rect x="320" y="320" width="16" height="20" rx="2" fill="#6366f1" />
            <text x="295" y="335" fill="#4f46e5" fontSize="10" fontWeight="700">Step 3</text>

            {/* Progression Step 3 -> Step 4 */}
            <path
              d="M 336 325 C 365 325, 375 330, 410 330 L 410 355 C 375 355, 365 340, 336 340 Z"
              fill="url(#gradient-flow-progression)"
            />

            {/* Step 4 Node */}
            <rect x="410" y="330" width="16" height="25" rx="2" fill="#6366f1" />
            <text x="385" y="347" fill="#4f46e5" fontSize="10" fontWeight="700">Step 4</text>

            {/* Drop-off from Step 4 to Exit */}
            <path
              d="M 426 330 C 600 330, 700 360, 850 360 L 850 372 C 700 372, 600 345, 426 345 Z"
              fill="url(#gradient-step-drop)"
            />
            <rect x="850" y="360" width="16" height="12" rx="2" fill="#ef4444" />
            <text x="874" y="370" fill="#dc2626" fontSize="11" fontWeight="600">Dropped off at Step 4</text>

            {/* Progression Step 4 -> Step 5 */}
            <path
              d="M 426 345 C 455 345, 465 350, 500 350 L 500 365 C 465 365, 455 355, 426 355 Z"
              fill="url(#gradient-flow-progression)"
            />

            {/* Step 5 Node */}
            <rect x="500" y="350" width="16" height="15" rx="2" fill="#6366f1" />
            <text x="475" y="362" fill="#4f46e5" fontSize="10" fontWeight="700">Step 5</text>

            {/* Progression Step 5 -> Step 6 */}
            <path
              d="M 516 352 C 545 352, 555 352, 590 352 L 590 365 C 555 365, 545 365, 516 365 Z"
              fill="url(#gradient-flow-progression)"
            />

            {/* Step 6 Node */}
            <rect x="590" y="352" width="16" height="13" rx="2" fill="#6366f1" />
            <text x="565" y="363" fill="#4f46e5" fontSize="10" fontWeight="700">Step 6</text>

            {/* Progression Step 6 -> Step 7 */}
            <path
              d="M 606 354 C 635 354, 645 355, 680 355 L 680 380 C 645 380, 635 365, 606 365 Z"
              fill="url(#gradient-flow-progression)"
            />

            {/* Step 7 Node */}
            <rect x="680" y="355" width="16" height="25" rx="2" fill="#6366f1" />
            <text x="655" y="372" fill="#4f46e5" fontSize="10" fontWeight="700">Step 7</text>

            {/* Drop-off from Step 7 to Exit */}
            <path
              d="M 696 355 C 750 355, 800 390, 850 390 L 850 402 C 800 402, 750 368, 696 368 Z"
              fill="url(#gradient-step-drop)"
            />
            <rect x="850" y="390" width="16" height="12" rx="2" fill="#ef4444" />
            <text x="874" y="400" fill="#dc2626" fontSize="11" fontWeight="600">Dropped off at Step 7</text>

            {/* Progression Step 7 -> Step 8 */}
            <path
              d="M 696 368 C 725 368, 735 375, 770 375 L 770 390 C 735 390, 725 380, 696 380 Z"
              fill="url(#gradient-flow-progression)"
            />

            {/* Step 8 Node */}
            <rect x="770" y="375" width="16" height="15" rx="2" fill="#6366f1" />
            <text x="745" y="387" fill="#4f46e5" fontSize="10" fontWeight="700">Step 8</text>

            {/* Progression Step 8 -> Step 9 */}
            <path
              d="M 786 375 C 800 375, 810 375, 830 375 L 830 390 C 810 390, 800 390, 786 390 Z"
              fill="url(#gradient-flow-progression)"
            />

            {/* Step 9 Node */}
            <rect x="830" y="375" width="16" height="15" rx="2" fill="#6366f1" />
            <text x="805" y="387" fill="#4f46e5" fontSize="10" fontWeight="700">Step 9</text>

            {/* Drop-off from Step 9 to Exit */}
            <path
              d="M 846 380 C 860 380, 870 415, 850 415 L 850 425 C 870 425, 860 390, 846 390 Z"
              fill="url(#gradient-step-drop)"
            />
            <rect x="850" y="415" width="16" height="12" rx="2" fill="#ef4444" />
            <text x="874" y="425" fill="#dc2626" fontSize="11" fontWeight="600">Dropped off at Step 9</text>
          </svg>
        </div>
      </div>
    </div>
  );
}

IapUsageSkeleton.propTypes = {
  featureName: PropTypes.string
};
